import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiEndpoints } from '@avisoma/shared';
import FileSize from '../../components/FileSize';
import { SectionCard } from './SectionCard';
import { QueryStateGuard } from './QueryStateGuard';
import { FileIcon } from './FileIcon';

type UploadQueryResult = UseQueryResult<ApiEndpoints<string>['/api/uploads/:id']['GET']['response']>;

export function InputDetailsSection({ uploadQuery }: { uploadQuery: UploadQueryResult }) {
  return (
    <QueryStateGuard query={uploadQuery} noDataMessage="No upload found.">
      {(data) => {
        const input = data.item?.input;
        if (!input) {
          return <div className="text-sm text-gray-500 dark:text-zinc-400">No upload found.</div>;
        }
        const urls = input.urls ?? [];
        const files = input.files ?? [];
        return (
          <SectionCard title="Input Details">
            {urls.length > 0 && (
              <div className="mb-4">
                <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 mb-1">URLs</dt>
                <ul className="list-disc list-inside text-gray-900 dark:text-zinc-100 marker:text-gray-500 dark:marker:text-zinc-400">
                  {urls.map((url: string, idx: number) => (
                    <li key={idx}>
                      <a
                        href={url}
                        className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {files.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 mb-1">Files</dt>
                <ul className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {files.map((file, idx: number) => (
                    <div
                      key={idx}
                      className="py-2 border border-gray-200 dark:border-zinc-600 rounded-md p-2 flex items-center gap-2"
                    >
                      <FileIcon mimeType={file.mimeType} />
                      <div className="flex flex-col overflow-hidden">
                        <span className="font-medium text-gray-900 dark:text-zinc-100 truncate">
                          {file.originalName || 'Unnamed file'}
                        </span>
                        {file.size !== undefined && (
                          <FileSize bytes={file.size} className="text-xs text-gray-500 dark:text-zinc-400" />
                        )}
                        {file.mimeType && (
                          <span className="text-xs text-gray-500 dark:text-zinc-400">{file.mimeType}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </ul>
              </div>
            )}

            {urls.length === 0 && files.length === 0 && (
              <div className="text-gray-500 dark:text-zinc-400">No input details available.</div>
            )}
          </SectionCard>
        );
      }}
    </QueryStateGuard>
  );
}
